<script setup>
// Astra DS · NavSheet — la navegación en móvil.
//
// Figma: `Móvil/Menú abierto` 814:4032, panel 814:4065.
//
// 🔴 **No es un modal centrado, es un panel pegado a la derecha.** 330 de los
// 390, dejando 60 por los que se ve la página que hay debajo. Esa franja no es
// decoración: dice que no te has ido a ninguna parte y da dónde tocar para
// volver.
//
// Sustituye al carril horizontal que había antes. Con seis secciones y una
// pantalla de 390, aquello era una lista que había que arrastrar para descubrir
// que existía: la mitad de los destinos no se veían nunca.
//
// 🔴 **Rehecho el 04.11.26 con el selector de idioma** (`Pattern/MobileMenu ·
// móvil 813:328`, DESIGN.md § En el móvil). El orden, de arriba abajo:
//
//   cabecera   «MENÚ» · idioma · tema · cerrar invertido, celdas de 52
//   secciones  las de siempre
//   cuenta     nuestra, no está en el nodo (ver abajo)
//   Radar      la invitación, aparte
//   apps       TestFlight y Google Play, que salieron de la cabecera
//   relleno    empuja el pie abajo
//   pie        solo las redes, en iconos
//
// Salieron «Suena ahora», el botón de tema del pie (subió a la cabecera) y las
// filas de texto de escuchar y redes.
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import BotonApp from "./BotonApp.vue";
import BanderaIdioma from "./BanderaIdioma.vue";
import MenuIdioma from "./MenuIdioma.vue";
import { useCuentaStore } from "@/stores/cuenta";
import { useI18n } from "vue-i18n";
import { IDIOMA, NOMBRE_DEL_IDIOMA, cambiaIdioma } from "@/i18n/idioma";

const { t } = useI18n();

const props = defineProps({
  open: { type: Boolean, default: false },

  // [{ label, to }] — las mismas que la cabecera de escritorio.
  // La misma lista que la cabecera. Los marcados con `soloEscritorio` no bajan
  // aquí: ver `destinos`.
  items: { type: Array, default: () => [] },
  current: { type: String, default: "" },

  theme: { type: String, default: "dark" },

  // Las redes del pie. Llegan por prop y no se escriben aquí: el pie del sitio
  // ya las tiene, y dos listas que hay que acordarse de cambiar a la vez acaban
  // diciendo cosas distintas.
  social: { type: Array, default: () => [] },
});

// ── El pie: las redes en iconos ── (`redes 2836:21`) En el orden del nodo:
// Bluesky · Instagram · YouTube · Facebook. El nombre de la red va como nombre
// accesible y `title`; no hay rótulo.
const ICONO_DE_LA_RED = { Bluesky: "bluesky", Instagram: "instagram", YouTube: "logo-youtube", Facebook: "facebook" };
const redes = computed(() =>
  Object.keys(ICONO_DE_LA_RED)
    .map((nombre) => props.social.find((r) => r.label === nombre))
    .filter(Boolean),
);

// ── El idioma ── La lista cuelga de la cabecera del menú, a todo su ancho, por
// encima de las secciones y sin moverlas. Se cierra al tocar el selector otra
// vez, al tocar fuera de la lista, al elegir y al cerrar el menú.
const idiomaAbierto = ref(false);
const botonIdioma = ref(null);
const nombreIdioma = computed(() => t("marco.cabecera.idioma", { idioma: NOMBRE_DEL_IDIOMA[IDIOMA] }));

function fueraDelIdioma(e) {
  if (botonIdioma.value?.contains(e.target)) return;
  if (e.target.closest?.(".nav-sheet__idiomas")) return;
  idiomaAbierto.value = false;
}

watch(idiomaAbierto, (abierto) => {
  if (abierto) document.addEventListener("pointerdown", fueraDelIdioma);
  else document.removeEventListener("pointerdown", fueraDelIdioma);
});

function elegirIdioma(idioma) {
  idiomaAbierto.value = false;
  cambiaIdioma(idioma);
}

// Los que sí bajan al panel. `soloEscritorio` deja fuera a Radar, que aquí ya
// tiene su celda propia al pie —«Radar →», junto al buzón— y ponerlo dos veces
// en la misma pantalla es ruido en el sitio donde menos sobra.
const destinos = computed(() =>
  props.items.filter((item) => !item.soloEscritorio),
);

const emit = defineEmits(["close", "toggle-theme"]);

// La sesión, para saber si la celda de cuenta dice «Entrar» o «Mi cuenta».
const cuenta = useCuentaStore();

const panel = ref(null);
const activo = (item) =>
  props.current === item.to ||
  (item.to !== "/" && String(props.current).startsWith(String(item.to))) ||
  (item.tambienEn || []).some((prefijo) => String(props.current).startsWith(prefijo));

// ── Lo que un panel a pantalla completa tiene que hacer ──
//
// El fondo no se mueve mientras el panel está abierto. Sin esto, arrastrar
// sobre el panel desplaza la página de debajo y al cerrar te has ido de sitio.
let scrollBloqueado = "";
function bloquearFondo(si) {
  if (typeof document === "undefined") return;
  if (si) {
    scrollBloqueado = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = scrollBloqueado;
  }
}

// Esc cierra primero la lista de idiomas, si está abierta, y devuelve el foco
// a su selector; si no, el menú.
function alTeclado(e) {
  if (e.key !== "Escape") return;
  if (idiomaAbierto.value) {
    idiomaAbierto.value = false;
    botonIdioma.value?.focus();
    return;
  }
  emit("close");
}

watch(
  () => props.open,
  async (abierto) => {
    bloquearFondo(abierto);
    if (abierto) {
      document.addEventListener("keydown", alTeclado);
      // El foco entra en el panel: si se queda en el botón de abrir, que está
      // detrás, el teclado sigue navegando por la página tapada.
      await nextTick();
      panel.value?.focus();
    } else {
      document.removeEventListener("keydown", alTeclado);
      idiomaAbierto.value = false;
    }
  },
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", alTeclado);
  document.removeEventListener("pointerdown", fueraDelIdioma);
  bloquearFondo(false);
});
</script>

<template>
  <Teleport to="body">
    <!-- La franja de la izquierda cierra al tocarla. Es lo que se espera de un
         panel que deja ver lo de detrás. -->
    <div v-if="open" class="nav-sheet" @click.self="emit('close')">
      <nav
        ref="panel"
        class="nav-sheet__panel"
        role="dialog"
        aria-modal="true"
        :aria-label="t('marco.menu.menu')"
        tabindex="-1"
      >
        <!-- ── La cabecera del menú ── (`cabecera 813:296`, 52 de alto) -->
        <div class="nav-sheet__head">
          <p class="nav-sheet__titulo">{{ t("marco.menu.menu") }}</p>

          <button
            ref="botonIdioma"
            type="button"
            class="nav-sheet__celda nav-sheet__idioma"
            :class="{ 'is-abierto': idiomaAbierto }"
            :aria-label="nombreIdioma"
            :title="nombreIdioma"
            aria-haspopup="menu"
            :aria-expanded="idiomaAbierto ? 'true' : 'false'"
            aria-controls="menu-idioma-movil"
            @click="idiomaAbierto = !idiomaAbierto"
          >
            <BanderaIdioma :idioma="IDIOMA" />
            <span class="nav-sheet__idioma-nombre">{{ NOMBRE_DEL_IDIOMA[IDIOMA] }}</span>
            <AstraIcon class="nav-sheet__flecha" name="chevron-down" :size="10" />
          </button>

          <!-- El tema enseña el modo al que se pasa: el sol en oscuro. -->
          <button
            type="button"
            class="nav-sheet__celda nav-sheet__tema"
            :aria-label="theme === 'dark' ? t('marco.tema.oscuroAClaro') : t('marco.tema.claroAOscuro')"
            :title="theme === 'dark' ? t('marco.tema.oscuroAClaro') : t('marco.tema.claroAOscuro')"
            @click="emit('toggle-theme')"
          >
            <AstraIcon :name="theme === 'dark' ? 'sun' : 'moon'" :size="20" />
          </button>

          <!-- Cerrar, invertido: se separa de idioma y tema sin usar el rojo,
               que es de directo y de acción principal. -->
          <button
            type="button"
            class="nav-sheet__cerrar"
            :aria-label="t('marco.menu.cerrar')"
            @click="emit('close')"
          >
            <AstraIcon name="close" :size="20" />
          </button>
        </div>

        <!-- La lista de idiomas (`Pattern/MenuIdioma · móvil 2836:89`). -->
        <div v-if="idiomaAbierto" class="nav-sheet__idiomas">
          <MenuIdioma id="menu-idioma-movil" movil @elegir="elegirIdioma" />
        </div>

        <ul class="nav-sheet__list">
          <li v-for="item in destinos" :key="item.to">
            <router-link
              class="nav-sheet__link"
              :class="{ 'is-on': activo(item) }"
              :to="item.to"
              @click="emit('close')"
            >
              <!-- Cuadrado, no punto ni subrayado: es la misma marca de «esto
                   está vivo» que usa la cabecera. -->
              <i v-if="activo(item)" class="nav-sheet__mark" aria-hidden="true" />
              {{ item.label }}
            </router-link>
          </li>
        </ul>

        <!-- ── La cuenta, en móvil ──
             El panel `AccountMenu` de la cabecera es solo web, así que aquí el
             acceso baja al menú con el molde del buzón. No se repite todo el
             panel: en una pantalla de 390 lo que hace falta es la puerta.

             ⚠️ **No está en el nodo `813:328`**, ni lo estaba antes: es la única
             puerta a la cuenta en el móvil y se queda. Preguntado a diseño el
             04.11.26. -->
        <router-link
          class="nav-sheet__buzon nav-sheet__buzon--quiet"
          :to="cuenta.haySesion ? '/cuenta/area' : '/cuenta'"
          @click="emit('close')"
        >
          <span class="nav-sheet__buzon-title">
            {{ cuenta.haySesion ? t("marco.menu.miCuenta") : t("marco.menu.entrar") }}
          </span>
          <span class="nav-sheet__buzon-sub">
            {{
              cuenta.haySesion
                ? cuenta.cuenta?.email || t("marco.menu.tuCuenta")
                : t("marco.menu.crearCuentaPide")
            }}
          </span>
        </router-link>

        <!-- El buzón no es una sección más: es lo único que se le pide a quien
             mira, así que va aparte y con su frase. -->
        <router-link
          class="nav-sheet__buzon"
          to="/radar"
          @click="emit('close')"
        >
          <span class="nav-sheet__buzon-title">{{ t("marco.menu.radar") }}</span>
          <span class="nav-sheet__buzon-sub">
            {{ t("marco.menu.radarSub") }}
          </span>
        </router-link>

        <!-- ── Las apps, bajo Radar ── Dos botones que reparten el ancho; el botón
             entero es el área táctil. -->
        <div class="nav-sheet__apps">
          <BotonApp class="nav-sheet__app" tienda="testflight" @navega="emit('close')" />
          <BotonApp class="nav-sheet__app" tienda="google-play" />
        </div>

        <div class="nav-sheet__fill" />

        <!-- ── El pie: solo las redes ── (`pie 813:317`) -->
        <div v-if="redes.length" class="nav-sheet__foot">
          <a
            v-for="red in redes"
            :key="red.label"
            class="nav-sheet__red"
            :href="red.href"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="red.label"
            :title="red.label"
          >
            <AstraIcon :name="ICONO_DE_LA_RED[red.label]" :size="20" />
          </a>
        </div>
      </nav>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.nav-sheet {
  position: fixed;
  inset: 0;
  // 🔴 Por encima del banner de cookies, que está en 9999.
  //
  // Con 120 el panel se pintaba debajo: se veía —el banner solo ocupa la franja
  // de abajo— pero el banner se llevaba los clics y la rueda en la zona que
  // cubre. Medido: `elementFromPoint` en el centro del panel devolvía el icono
  // de la cookie, no el menú. Ni se podía deslizar ni pulsar ahí.
  //
  // Es de esos fallos que una prueba automática no ve si hace `click()` por
  // JavaScript, porque eso se salta el hit-testing. Solo aparece pulsando de
  // verdad, que es lo que hace una persona.
  //
  // Se queda por debajo del modal de edad (10000), que es bloqueante: si ese
  // está delante, no hay nada que navegar todavía.
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
  // Se oscurece lo de detrás, pero poco: la franja tiene que seguir leyéndose
  // como la página, no como un telón.
  background: rgb(0 0 0 / 55%);
}

.nav-sheet__panel {
  display: flex;
  flex-direction: column;
  width: 330px;
  max-width: calc(100% - 60px);
  height: 100%;
  box-sizing: border-box;
  // El lienzo, no la superficie elevada: el panel no flota sobre nada, ocupa
  // su sitio. Y ata el fondo explícitamente — transparente resolvería contra
  // lo que hubiera detrás.
  // `relative` para colgar la lista de idiomas de la cabecera.
  position: relative;
  background: var(--color-bg-canvas);
  border-left: 1px solid var(--color-border-default);
  overflow-y: auto;
  // 🔴 `padding: 0` no es decorativo, es un reset obligatorio.
  //
  // Este panel es un `<nav>`, y astra-styles.scss —la capa legacy que C1
  // congeló— tiene un `nav { padding: 1.125rem }` global. Sin esto, el panel
  // heredaba 18px por los CUATRO lados: separaba todo del borde, empujaba las
  // filas hacia dentro y dejaba aire de sobra arriba y abajo. Medido: los
  // textos caían en x=39 en vez de x=20.
  //
  // Es el mismo fallo que ya documenta SiteHeader, y volvió a pasar porque el
  // componente nuevo también usa `<nav>`. Muere cuando C4 vacíe esa capa.
  padding: 0;

  &:focus {
    outline: none;
  }
}

.nav-sheet__head {
  display: flex;
  flex: none;
  align-items: stretch;
  height: 52px;
  padding: 0 0 0 20px;
  border-bottom: 1px solid var(--color-border-default);
}

// «MENÚ» a `mono/label-m` en primario (04.11.26); ocupa el ancho que sobra.
.nav-sheet__titulo {
  @include t.mono-label-m;
  flex: 1 1 0;
  align-self: center;
  min-width: 0;
  margin: 0;
  color: var(--color-text-primary);
}

// Las tres celdas de 52 de alto, cada una con su filete a la izquierda (cerrar
// no lo lleva: lo separa la inversión).
.nav-sheet__celda,
.nav-sheet__cerrar {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.nav-sheet__celda {
  border-left: 1px solid var(--color-border-default);
  color: var(--color-icon-secondary);
}

// Idioma: 14 a los lados y 8 entre piezas. Sin la palabra «Idioma».
.nav-sheet__idioma {
  gap: 8px;
  padding: 0 14px;

  &.is-abierto .nav-sheet__flecha {
    color: var(--color-icon-accent);
    transform: rotate(180deg);
  }
}

.nav-sheet__idioma-nombre {
  @include t.mono-nano;
  color: var(--color-text-primary);
  white-space: nowrap;
}

.nav-sheet__tema {
  width: 52px;
}

// Cerrar, invertido: relleno `text/primary` y el aspa en `bg/canvas`.
.nav-sheet__cerrar {
  width: 52px;
  background: var(--color-text-primary);
  color: var(--color-bg-canvas);

  &:focus-visible {
    outline-color: var(--color-action-primary-default);
  }
}

// La lista cuelga del filete inferior de la cabecera, a todo el ancho, por
// encima de las secciones y sin moverlas.
.nav-sheet__idiomas {
  position: absolute;
  top: 52px;
  right: 0;
  left: 0;
  z-index: 2;
}

.nav-sheet__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

// 51 de alto por fila, del nodo. Por encima de los 44 que pide un objetivo
// táctil, que era lo que había que comprobar antes de copiar la medida.
.nav-sheet__link {
  @include t.display-item;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 51px;
  padding: 0 20px;
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-primary);
  text-decoration: none;

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.nav-sheet__mark {
  flex: none;
  width: 7px;
  height: 7px;
  background: var(--color-action-primary-default);
}

// ⚠️ La sección activa arranca 17px más adentro que las demás, porque el
// cuadrado va EN EL FLUJO y empuja al texto. No es un descuido: es lo que hace
// el nodo —rectángulo en x=20, texto en x=37, y el resto de secciones en 20—.
//
// Sacarlo del flujo alinearía las siete, pero entonces el cuadrado se comería
// el margen de 20 o se saldría del panel. Si algún día molesta, la decisión es
// de diseño y se toma en la Library, no aquí.

.nav-sheet__buzon {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border-default);
  color: var(--color-text-primary);
  text-decoration: none;

  &:hover {
    background: var(--color-bg-elevated);
  }
}

.nav-sheet__buzon-title {
  @include t.display-item;
  color: var(--color-text-accent);
}

// El rojo es del buzón, que es la invitación. La cuenta usa la misma celda en
// voz normal: si las dos gritan, ninguna destaca.
.nav-sheet__buzon--quiet .nav-sheet__buzon-title {
  color: var(--color-text-primary);
}

.nav-sheet__buzon-sub {
  @include t.mono-nano;
  color: var(--color-text-tertiary);
}

// Empuja el pie abajo cuando sobra alto, y desaparece cuando no.
.nav-sheet__fill {
  flex: 1 1 auto;
  min-height: 24px;
}

// ── Las apps ── 56 de alto, filete abajo y entre las dos.
.nav-sheet__apps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid var(--color-border-default);
}

.nav-sheet__app {
  height: 55px;
  padding: 0 20px;

  & + & {
    border-left: 1px solid var(--color-border-default);
  }

  &:hover {
    background: var(--color-bg-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

// ── El pie ── Cuatro celdas iguales de 44 con filete entre ellas.
.nav-sheet__foot {
  display: flex;
  flex: none;
  border-top: 1px solid var(--color-border-default);
}

.nav-sheet__red {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  height: 44px;
  color: var(--color-icon-secondary);

  & + & {
    border-left: 1px solid var(--color-border-default);
  }

  &:hover {
    color: var(--color-icon-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .nav-sheet__panel {
    animation: nav-sheet-in 0.18s ease-out;
  }
}

@keyframes nav-sheet-in {
  from {
    transform: translateX(12px);
    opacity: 0.6;
  }
}
</style>
